import { Capacitor } from '@capacitor/core';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { progressApi } from '@/api/progress';
import { leaveLesson } from '@/lib/lessonExit';
import { scrollSectionTop } from '@/lib/sectionScroll';
import { openItemsAnswerText } from '@/lib/openItems';
import { coursesApi, type Block, type BlockType, type Lesson, type Section } from '@/api/courses';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePageVisibility } from '@/hooks/usePageVisibility';
import { humanizeError } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';
import { lessonPhoneLayout } from '@/lib/lessonPhoneLayout';
import { isStoreBuild } from '@/lib/platform';
import { LessonSidebar } from '../desktop/LessonSidebar';

import { BlockView, type BlockReview } from '@/components/lesson/BlockView';
import { BlockBoundary } from '@/components/lesson/BlockBoundary';
import { SectionsNav } from '@/components/lesson/SectionsNav';
import { MChips, MTitle } from '@/components/mobile/MobileKit';
import { computeNumbers } from '@/components/lesson/blockNumbering';
import { answerChecked } from '@/lib/haptics';
import { enqueue, isOutboxStuck, onOutboxChange, onOutboxStuck, queuedOfKind } from '@/lib/outbox';
import { LESSON_AUDIO_KIND, buildLessonAudioPayload, lessonAudioId, type LessonAudioPayload } from '@/lib/lessonAudio';
import { announceRecordingQueued } from '@/lib/lessonAudioSend';
import { apiErrorBlockIds, apiErrorCode } from '@/lib/apiErrorCode';
import {
  ANSWERABLE_TYPES,
  OPEN_TASK_TYPES,
  applyOpenAnswersMissing,
  createSectionAutoComplete,
  sectionHasOpenTasks,
  sectionsToFire,
  stuckOnOpenTasks,
} from '@/lib/sectionCompletion';
import { blockHasContent } from '@/lib/blockContent';
import { titleHtml, titleText } from '@/lib/blockTitle';
import { findSectionSubmission, pickInitialSection, sectionNotice, type SectionNotice } from '@/lib/homeworkResubmit';
import { reviewNoteOf } from '@/lib/studentFields';
import { useLeaveWhileRecording } from '@/hooks/useLeaveWhileRecording';
import { SectionResubmitNotice } from './SectionResubmitNotice';
import { OpenTasksHoldNotice } from './OpenTasksHoldNotice';

/**
 * Student lesson player — Edvibe-style single-scroll lesson.
 *
 * All sections render stacked; the right-hand "Разделы" nav jumps between them
 * and highlights the one in view. A section auto-completes (progressApi.
 * completeSection) once it has been viewed AND all its required blocks are
 * answered — no explicit "finish" button. The homework section (is_homework)
 * is pulled out and shown separately at the bottom. When every normal section
 * is complete, a completion banner appears.
 */

// Какие задания обязательны для закрытия раздела — `lib/sectionCompletion.ts`
// (там же `audio_record`, добавленный 10.09.2026, и объяснение, зачем).


/**
 * Разбор урока под чужой учёткой — куратором или админом.
 *
 * ⚠️ ПРИСУТСТВИЕ ЭТОГО ОБЪЕКТА ЗНАЧИТ «ТОЛЬКО ЧТЕНИЕ». Он не косметика: по
 * нему выключаются ВСЕ пять мест, где плеер пишет на сервер. Забыть хоть одно
 * — значит записать чужому ученику прогресс от лица смотрящего, и заметить это
 * будет нечем: ни ошибки, ни следа, просто у ученика однажды «сам собой»
 * закрылся урок.
 */
export type LessonReview = {
  /** Чей урок разбираем. */
  studentId: string;
  /** Куда возвращает «назад» — список уроков этого ученика. */
  backTo: string;
  /** Имя ученика для шапки разбора на телефоне — она одна вместо двух (docs/CURATOR-MOBILE-DESIGN.md §4.2). */
  studentName?: string;
};

export function StudentLessonPlayerPage({ review }: { review?: LessonReview } = {}) {
  const { t } = useTranslation();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  // Запись голоса идёт или на паузе — уход со страницы спрашивает (задача 6).
  useLeaveWhileRecording();

  // В приложении на iOS урок всегда телефонный — разбор в lib/lessonPhoneLayout.ts.
  const isMobile = lessonPhoneLayout({
    narrow: useIsMobile(),
    store: isStoreBuild(),
    platform: Capacitor.getPlatform(),
  });

  // Куда ведёт «назад»: ученику — к своим урокам, сотруднику — к списку уроков
  // разбираемого ученика.
  const backTo = review?.backTo ?? '/app/lessons';
  // ⚠️ «НАЗАД» И «ЗАВЕРШИТЬ УРОК» ВЫВОДЯТ ИЗ УРОКА ТУДА, ОТКУДА В НЕГО ВОШЛИ (02.10.2026, `lib/lessonExit.ts`):
  // на страницу курса, главную, ленту — а не всегда на список уроков шагом вперёд, после которого «назад»
  // возвращал в закрытый урок. Цепочка уроков (пуш из открытого урока) проходится целиком. Разбор урока
  // сотрудником — по-прежнему переход к списку уроков ученика.
  const exitLesson = useCallback(() => {
    if (review) navigate(backTo);
    else leaveLesson(navigate, backTo);
  }, [review, navigate, backTo]);
  const onExitLink = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      exitLesson();
    },
    [exitLesson],
  );

  const lesson = useQuery({
    queryKey: ['student', 'lesson', lessonId],
    queryFn: () => coursesApi.getStudentLesson(lessonId!),
    enabled: !!lessonId,
  });

  // Previously-submitted answers for this lesson — so a page refresh RESTORES
  // the student's work instead of showing a blank lesson.
  //
  // ⚠️ УЧЕНИК ВХОДИТ В КЛЮЧ КЭША. Иначе куратор, посмотрев одного ученика,
  // увидел бы его ответы у следующего — и у себя самого.
  const savedAnswers = useQuery({
    queryKey: ['student', 'lesson-answers', review?.studentId ?? 'me', lessonId],
    queryFn: () =>
      review
        ? progressApi.getStudentLessonAnswers(review.studentId, lessonId!)
        : progressApi.getMyLessonAnswers(lessonId!),
    enabled: !!lessonId,
  });

  // Пройденные разделы — с сервера, а не только из памяти вкладки.
  // Раньше список начинался пустым при каждом открытии урока: ученик,
  // вернувшийся назавтра, видел «Пройдено разделов: 0 / 9» при восьми
  // пройденных и не понимал, почему урок не закрывается.
  const completedSections = useQuery({
    queryKey: ['student', 'lesson-completed-sections', review?.studentId ?? 'me', lessonId],
    queryFn: () =>
      review
        ? progressApi.getStudentCompletedSections(review.studentId, lessonId!)
        : progressApi.getCompletedSections(lessonId!),
    enabled: !!lessonId,
  });

  const lessonMarathonId = (lesson.data as (Lesson & { marathon_id?: string }) | undefined)?.marathon_id;
  useEffect(() => {
    // ⚠️ РАЗБОР НЕ НАЧИНАЕТ УРОК ЗА УЧЕНИКА. Куратор, открывший урок, завёл бы
    // ученику отметку «урок начат» — и в отчётах появился бы прогресс, которого
    // человек не делал.
    if (review) return;
    if (!lessonId || !lessonMarathonId) return;
    progressApi.startLesson(lessonId, lessonMarathonId).catch(() => {});
  }, [review, lessonId, lessonMarathonId]);

  const sections: Section[] = useMemo(
    () => (lesson.data?.sections ?? []).slice().sort((a, b) => a.order_index - b.order_index),
    [lesson.data],
  );
  const normalSections = sections.filter((s) => !s.is_homework);
  const homework = sections.find((s) => s.is_homework);
  // Paginated section flow (Edvibe-style): the student sees ONE section at a
  // time and advances with a Next control, instead of one long scroll. Homework
  // is the last page.
  const pages = useMemo(
    () => (homework ? [...normalSections, homework] : normalSections),
    [normalSections, homework],
  );
  const numbers = useMemo(() => computeNumbers(sections), [sections]);

  // «Переделать» из «Домашки» открывает урок сразу на разделе сдачи (`?section=`).
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');

  // ⚠️ СДАЧИ ДЛЯ ПЛАШКИ ПЕРЕСДАЧИ — ПО ТРЁМ СТАТУСАМ, А НЕ «ПОСЛЕДНИЕ 100». Список
  // идёт от новых к старым, и работа, вернувшаяся на доработку месяц назад, у
  // ученика с сотней сдач в общий список не попала бы — плашка молча пропала бы.
  // Префикс ключа — ['student','homework']: его сбрасывает очередь отправки после
  // ответа, и «на доработку» само становится «отправлено заново». Разбору урока
  // куратором чужая домашка ни к чему — запроса нет.
  const hasOpenTasks = normalSections.some((s) => sectionHasOpenTasks(s));
  const resubmitRows = useQuery({
    queryKey: ['student', 'homework', 'resubmit-notice'],
    queryFn: async () => {
      const lists = await Promise.all(
        (['revision', 'rejected', 'pending'] as const).map((s) => progressApi.listMyHomework(s, 0, 100)),
      );
      return lists.flatMap((l) => l.items);
    },
    enabled: !review && hasOpenTasks,
  });

  // ---- answer + completion state (lifted so nav can show ✓ per section) ----
  const [answered, setAnswered] = useState<Record<string, boolean>>({});
  // Raw answer payloads per block, so a homework section can bundle its essay
  // text + recorded audio into ONE submission for the curator.
  const [blockAnswers, setBlockAnswers] = useState<Record<string, { answer_data: string; block_type?: string }>>({});
  const [viewed, setViewed] = useState<Set<string>>(new Set());
  /**
   * Разделы, которые сервер подтвердил пройденными.
   *
   * ⚠️ СЮДА ПОПАДАЕТ ТОЛЬКО ПОДТВЕРЖДЁННОЕ. Раньше отметка ставилась при любом
   * исходе: `enqueue` не бросает, поэтому `onSuccess` срабатывал и на 500.
   * 07.09.2026 ученица видела три галочки и баннер «урок пройден», когда на
   * сервере не записалось ничего, — и не поняла, почему курс заперт.
   */
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  /** Разделы, отметка которых лежит в очереди и сервером ещё не принята. */
  const [pendingSections, setPendingSections] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  /** Какие разделы автозакрытие уже отметило или ждёт нового ответа после 409 — `lib/sectionCompletion.ts`. */
  const [autoComplete] = useState(createSectionAutoComplete);

  // block_id → saved answer, passed into each block so it can restore its own UI
  // (selected option, typed text, …) after a refresh.
  const savedMap = useMemo(() => {
    const m: Record<string, { answer_data: string; is_correct: boolean }> = {};
    for (const a of savedAnswers.data ?? []) m[a.block_id] = { answer_data: a.answer_data, is_correct: a.is_correct };
    return m;
  }, [savedAnswers.data]);

  // block_id → как ученик прошёл задание. Нужен только разбору: ученику
  // собственные попытки не показываем, ему важен ответ, а не статистика.
  const reviewMap = useMemo(() => {
    const m: Record<string, BlockReview> = {};
    if (!review) return m;
    for (const a of savedAnswers.data ?? []) {
      m[a.block_id] = { attempts: a.attempts ?? 1, answeredAt: a.updated_at };
    }
    return m;
  }, [review, savedAnswers.data]);
  // Hydrate the lifted answer/completion state ONCE from the server, so section
  // ticks + homework bundling survive a reload. Runs only on first data arrival
  // and never clobbers answers the student gives in this session.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    const data = savedAnswers.data;
    if (!data || data.length === 0) return;
    hydratedRef.current = true;
    setAnswered((m) => {
      const n = { ...m };
      for (const a of data) n[a.block_id] = true;
      return n;
    });
    setBlockAnswers((m) => {
      const n = { ...m };
      for (const a of data) n[a.block_id] = { answer_data: a.answer_data, block_type: a.block_type };
      return n;
    });
  }, [savedAnswers.data]);

  // Подставляем пройденное с сервера. Список только пополняется — то, что
  // ученик прошёл в этой сессии, не затирается.
  //
  // ⚠️ РАНЬШЕ ЭТО РАБОТАЛО РОВНО ОДИН РАЗ. Теперь запрос перечитывается, когда
  // очередь дошлёт отложенную отметку, и галочка появляется по ответу сервера,
  // а не по нашей вере в него.
  useEffect(() => {
    const ids = completedSections.data;
    if (!ids?.length) return;
    setCompleted((c) => {
      const n = new Set(c);
      ids.forEach((id) => n.add(id));
      return n;
    });
    setPendingSections((p) => {
      if (!ids.some((id) => p.has(id))) return p;
      const n = new Set(p);
      ids.forEach((id) => n.delete(id));
      return n;
    });
    // Уже пройденные разделы не нужно завершать повторно.
    ids.forEach((id) => autoComplete.markFired(id));
  }, [completedSections.data, autoComplete]);

  // Очередь опустела — спрашиваем сервер заново: возможно, отложенная отметка
  // дошла, и раздел уже пройден.
  // Зависимость — именно `refetch`: он устойчив между отрисовками, а сам объект
  // запроса меняется каждый раз, и подписка пересоздавалась бы вхолостую.
  const refetchCompleted = completedSections.refetch;
  useEffect(
    () =>
      onOutboxChange((count) => {
        if (count === 0) void refetchCompleted();
      }),
    [refetchCompleted],
  );

  // Связь есть, а сервер отказывает несколько попыток подряд — ученику об этом
  // говорим прямо, а не прячем за полоской «ждут отправки».
  const [syncStuck, setSyncStuck] = useState(isOutboxStuck());
  useEffect(() => onOutboxStuck(setSyncStuck), []);

  // Задания, которые сервер назвал незаполненными при закрытии раздела.
  const [missingBlocks, setMissingBlocks] = useState<Set<string>>(new Set());

  /**
   * Сервер отказался закрыть раздел: в нём открытые задания без ответа
   * (`409 open_answers_missing`, договор `empty-open-answers.md`).
   *
   * ⚠️ С этих заданий снимается «отвечено». Иначе раздел по-прежнему считался бы
   * заполненным, автозакрытие не перезапустилось бы после ответа, а ученик видел
   * бы раздел, который «не закрывается» без объяснения.
   *
   * ⚠️ ОТКАЗ ЗАПИСЫВАЕТСЯ В ПРАВИЛО АВТОЗАКРЫТИЯ ПЕРВЫМ (техдолг п. 162): после 409
   * раздел ждёт нового ответа. Смена `answered` ниже иначе снова запустила бы
   * автозакрытие — и так по кругу, до 17 запросов в секунду.
   */
  const onSectionRejected = (sectionId: string, err: unknown) => {
    autoComplete.markRejected(sectionId, apiErrorCode(err));
    if (apiErrorCode(err) !== 'open_answers_missing') return;
    const ids = apiErrorBlockIds(err) ?? [];
    setMissingBlocks(new Set(ids));
    setAnswered((m) => applyOpenAnswersMissing(m, ids));
  };

  const completeSection = useMutation({
    // Через очередь: отметка «раздел пройден» переживает обрыв связи и уходит
    // позже. Иначе ученик проходил урок в марафоне, а он оставался незакрытым.
    //
    // Возвращает `true`, только если сервер принял отметку прямо сейчас.
    // `rejectOnPermanent`: отказ сервера (409 — задания не заполнены) приходит
    // сюда, а не прячется неделю за «ждёт отправки».
    mutationFn: (sectionId: string) =>
      enqueue('section-complete', `section:${sectionId}`, { sectionId }, { rejectOnPermanent: true }),
  });

  /**
   * Разбирает исход отметки: подтверждено — галочка, нет — «ждёт отправки».
   *
   * ⚠️ ОТМЕТКУ «УЖЕ ОТПРАВЛЯЛИ» НЕ СНИМАЕМ. Запись уже лежит в очереди, и очередь
   * сама её дошлёт; повторный выстрел из плеера только удвоил бы запросы.
   */
  const settleSection = (sectionId: string, delivered: boolean) => {
    if (delivered) {
      setCompleted((c) => new Set(c).add(sectionId));
      setPendingSections((p) => {
        if (!p.has(sectionId)) return p;
        const n = new Set(p);
        n.delete(sectionId);
        return n;
      });
      return;
    }
    setPendingSections((p) => new Set(p).add(sectionId));
  };

  // Автозакрытие: просмотренный раздел, где отвечено всё обязательное, отмечается
  // один раз. Пустые заготовки заданий не требуются (`requiredBlocks`); после 409
  // раздел ждёт нового ответа ученика (`createSectionAutoComplete`, техдолг п. 162).
  useEffect(() => {
    // ⚠️ РАЗБОР НЕ ЗАКРЫВАЕТ РАЗДЕЛЫ ЗА УЧЕНИКА. Куратор пролистал урок —
    // разделы отметились пройденными, а в последовательном курсе открылся
    // следующий урок. Ученик получил бы чужой прогресс молча.
    if (review) return;
    for (const sec of sectionsToFire(normalSections, viewed, answered, autoComplete)) {
      autoComplete.markFired(sec.id);
      completeSection.mutate(sec.id, {
        onSuccess: (delivered) => settleSection(sec.id, delivered),
        onError: (err) => onSectionRejected(sec.id, err),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answered, viewed, normalSections.length]);

  // Default to the first page; mark a section "viewed" the moment it becomes the
  // active page (this drives per-section completion tracking).
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!activeId && pages.length > 0) setActiveId(pickInitialSection(pages, sectionParam));
  }, [pages, activeId, sectionParam]);
  useEffect(() => {
    if (activeId) setViewed((v) => (v.has(activeId) ? v : new Set(v).add(activeId)));
  }, [activeId]);

  // Heartbeat for the active section's first block (time-on-content), paused
  // when the tab is hidden. Also pause media on hide.
  const visible = usePageVisibility();
  const activeFirstBlock = sections.find((s) => s.id === activeId)?.blocks?.[0]?.id ?? sections[0]?.blocks?.[0]?.id;
  useEffect(() => {
    // ⚠️ РАЗБОР НЕ ЗАСЧИТЫВАЕТ УЧЕНИКУ ВРЕМЯ. Время куратора, потраченное на
    // проверку, приписалось бы ученику как время на задании — и заодно завело
    // бы в таблице ответов пустые строки на блоки, которых он не касался.
    if (review) return;
    if (!activeFirstBlock || !visible) return;
    const id = setInterval(() => progressApi.heartbeat(activeFirstBlock), 30_000);
    return () => clearInterval(id);
  }, [review, activeFirstBlock, visible]);
  useEffect(() => {
    if (visible) return;
    document.querySelectorAll<HTMLMediaElement>('video, audio').forEach((el) => {
      try { el.pause(); } catch { /* cross-origin iframe pause may throw */ }
    });
  }, [visible]);

  const [pendingBlocks, setPendingBlocks] = useState<Set<string>>(new Set());

  // Очередь опустела — значит отложенные ответы (в том числе голосовая запись)
  // дошли до сервера. Перечитываем ответы, чтобы на странице появилась
  // настоящая ссылка на запись: до этого у блока была только сама запись, и
  // отправить домашнее задание было нечем.
  useEffect(() => {
    return onOutboxChange((count) => {
      if (count > 0) return;
      setPendingBlocks(new Set());
      void savedAnswers.refetch();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  // 🔴 ОТМЕТКИ «ЖДЁТ ОТПРАВКИ» — ИЗ ОЧЕРЕДИ, А НЕ ТОЛЬКО ИЗ ЭТОГО СЕАНСА (02.10.2026, дыра 4 сверки). После
  // перезапуска приложения записи голоса по-прежнему лежат на устройстве, а набор был пуст: раздел
  // выглядел так, будто ответа нет вовсе.
  useEffect(() => {
    let alive = true;
    void queuedOfKind(LESSON_AUDIO_KIND).then((items) => {
      if (!alive || !items.length) return;
      setPendingBlocks((s) => {
        const next = new Set(s);
        items.forEach((i) => next.add((i.payload as LessonAudioPayload).blockId));
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [lessonId]);

  const onAnswer = async (
    blockId: string,
    payload: {
      answer_data: string;
      is_correct: boolean;
      block_type?: string;
      blob?: Blob;
      // Часы страницы и причина остановки (12.09.2026, договор `record-integrity.md`
      // §2.1). Идут только с голосовым ответом и только вместе с `blob`.
      clientSeconds?: number;
      stopReason?: string;
      // Имя записи и момент «Стоп» (02.10.2026, договор `audio-upload.md` §1).
      clientId?: string;
      stoppedAt?: number;
    },
  ) => {
    // Единственная точка, через которую проходят ответы во всех типах упражнений
    // урока, — поэтому отклик ставится здесь, а не в каждом блоке отдельно.
    answerChecked(payload.is_correct);
    // Ответ сперва ложится в очередь и только потом уходит на сервер. Раньше
    // здесь стоял `.catch(() => {})`: без сети ответ пропадал молча, а ученику
    // всё равно ставилась галочка «сохранено».
    const { blob, clientSeconds, stopReason, clientId, stoppedAt, ...answer } = payload;
    // Голосовой ответ идёт своим видом действия: ссылки на запись ещё нет, есть
    // сам файл. Очередь сперва загрузит его, а потом отметит ответ — до задачи
    // 120 запись уходила мимо очереди и при обрыве связи пропадала насовсем.
    // Байты записи (или файл на телефоне, если включена «Запись без связи») собирает `lib/lessonAudio.ts`.
    // Часы без пауз, причина остановки, имя записи и момент «Стоп» едут ВМЕСТЕ С ЗАПИСЬЮ, а не отдельным
    // запросом: ответ может пролежать в очереди до возвращения связи, и к моменту отправки на странице от
    // этой записи не останется ничего — блок давно снят с экрана.
    // Урок и раздел — для «Неотправленных записей» в профиле и «Сдать» в «Домашках» (промпт 22, 18.10.2026): к моменту,
    // когда ученик откроет профиль, урока на экране не будет, а запись должна сказать, откуда она.
    const audioSection = blob ? sections.find((s) => (s.blocks ?? []).some((b) => b.id === blockId)) : undefined;
    const audioPayload = blob
      ? await buildLessonAudioPayload({
          blockId,
          blob,
          blockType: answer.block_type,
          clientSeconds,
          stopReason,
          clientId,
          stoppedAt,
          lessonId,
          lessonTitle: lesson.data?.title,
          sectionId: audioSection?.id,
          sectionTitle: audioSection?.title,
        })
      : null;
    // Серверу — сразу, до самой загрузки: он запоминает момент записи по своим часам (договор
    // `audio-upload.md` §4). Только при включённом флаге; отказ известия глотается молча.
    if (audioPayload && blob) announceRecordingQueued(audioPayload, blob.size);
    const delivered = audioPayload
      ? await enqueue(
          LESSON_AUDIO_KIND,
          lessonAudioId(blockId),
          audioPayload,
          // `requireStored`: хранилище отказало и на сервер не ушло — это ошибка у задания, а не
          // «сохранено на устройстве» (`OutboxStorageError`).
          { rejectOnPermanent: true, requireStored: true },
        )
      : // ⚠️ Отказ сервера ждём только у открытых заданий (эссе): у тестов и
        // упражнений `onAnswer` зовётся без `await`, и брошенная ошибка ушла бы
        // в пустоту. При отказе строки ниже не выполнятся — «отвечено» не ставится.
        await enqueue('lesson-answer', `lesson-answer:${blockId}`, { blockId, ...answer }, {
          rejectOnPermanent: OPEN_TASK_TYPES.has(answer.block_type as BlockType),
        });
    if (!delivered) setPendingBlocks((s) => new Set(s).add(blockId));
    // Ученик изменил ответ — раздел, ждавший после 409, снова можно отметить.
    // Строго до `setAnswered`: перерисовка запустит автозакрытие.
    autoComplete.answerChanged(sections.find((s) => (s.blocks ?? []).some((b) => b.id === blockId))?.id);
    setAnswered((m) => ({ ...m, [blockId]: true }));
    setMissingBlocks((s) => {
      if (!s.has(blockId)) return s;
      const next = new Set(s);
      next.delete(blockId);
      return next;
    });
    setBlockAnswers((m) => ({ ...m, [blockId]: { answer_data: answer.answer_data, block_type: answer.block_type } }));
    return delivered;
  };

  // Прокрутка к началу — после того, как новый раздел нарисован (`lib/sectionScroll.ts`): плавная прокрутка в
  // момент смены раздела обрывалась в WKWebView, и раздел открывался внизу (сборка 1.1.1, 05.10.2026).
  const scrollAfterSwitch = useRef(false);
  useEffect(() => {
    if (!scrollAfterSwitch.current) return;
    scrollAfterSwitch.current = false;
    scrollSectionTop(contentRef.current);
  }, [activeId]);

  const goToSection = (id: string) => {
    if (id === activeId) {
      scrollSectionTop(contentRef.current);
      return;
    }
    scrollAfterSwitch.current = true;
    setActiveId(id);
  };

  if (lesson.isLoading)
    return (
      <Card variant="subtle" className="flex items-center justify-center gap-2 py-10 text-on-surface-variant">
        <span className="material-symbols-outlined animate-spin">progress_activity</span>
        {t('lessonPlayer.loading')}
      </Card>
    );
  if (lesson.error || !lesson.data) {
    return (
      <Card variant="subtle" className="text-error">
        {lesson.error ? humanizeError(lesson.error) : t('lessonPlayer.unavailable')}{' '}
        <Link to={backTo} onClick={onExitLink} className="underline">{t('lessonPlayer.backToLessons')}</Link>
      </Card>
    );
  }

  const doneCount = normalSections.filter((s) => completed.has(s.id)).length;
  const allDone = normalSections.length > 0 && doneCount === normalSections.length;
  const activeIndex = Math.max(0, pages.findIndex((s) => s.id === activeId));
  const activeSection = pages[activeIndex];
  // Плашка — только над эссе и записью. У раздела домашки своя панель со статусом
  // и комментарием ниже; вторая плашка повторяла бы её.
  const activeNotice =
    !review && activeSection && !activeSection.is_homework && sectionHasOpenTasks(activeSection)
      ? sectionNotice(findSectionSubmission(resubmitRows.data, activeSection.id))
      : null;
  // Какое задание держит раздел незакрытым (26.09.2026, вариант В) — по ответам с сервера:
  // только там есть `attempts`, по которому пульс отличается от ответа. `OpenTasksHoldNotice`.
  const holding = review
    ? []
    : stuckOnOpenTasks(normalSections, completed, Object.fromEntries((savedAnswers.data ?? []).map((a) => [a.block_id, a])));

  const markDone = () => {
    if (!activeSection || completed.has(activeSection.id)) return;
    completeSection.mutate(activeSection.id, {
      onSuccess: (delivered) => settleSection(activeSection.id, delivered),
      // Та же проверка, что у автозакрытия: сервер назовёт незаполненные задания.
      onError: (err) => onSectionRejected(activeSection.id, err),
    });
  };

  // ── Компьютер: раскладка по макету «Desktop Web v2» ─────────────────────
  //
  // ⚠️ ТЕЛЕФОН ОСТАЁТСЯ НА ПРЕЖНЕЙ РАЗМЕТКЕ, она ниже. У этой страницы была
  // ОДНА разметка на оба размера, поэтому переделка «как в макете» задела бы и
  // приложение, а его в этот заход не трогаем.
  //
  // ⚠️ ДВЕ РАСКЛАДКИ — ОДИН ПУТЬ ДО ЗАДАНИЙ (01.10.2026). Набранный, но не проверенный ответ живёт в состоянии
  // блока. Раскладки меняются на ходу (поворот iPhone 12+ даёт ширину ≥ 768), и пока у веток были разные корни
  // (`<div>` и фрагмент), React сносил всё поддерево: набранное пропадало, ролик закрывался. Поэтому у обеих веток
  // корень одного типа, а звенья пути до `SectionBlock` и `HomeworkSubmitPanel` несут одинаковые `key` —
  // React узнаёт те же узлы и оставляет их состояние. Меняя разметку, путь не рвать:
  // сторож — `lessonLayoutSwitchKeepsAnswers.test.ts`.
  if (!isMobile) {
    return (
      <div>
        <Link
          to={backTo}
          onClick={onExitLink}
          className="mb-4 flex w-max items-center gap-[9px] text-[12.5px] font-semibold text-on-surface-variant hover:text-on-surface"
        >
          <span className="material-symbols-outlined text-[15px]">chevron_left</span>
          {lesson.data.title}
        </Link>

        <div key="lesson-grid" className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div key="lesson-content" ref={contentRef} className="min-w-0">
            {pages.length === 0 ? (
              <div className="rounded-[20px] bg-surface-container px-[18px] py-4 text-[13px] text-on-surface-variant">
                {t('lessonPlayer.noSections')}
              </div>
            ) : (
              <>
                {/* Шапка раздела: какой это шаг, название и отметка о том,
                    что раздел пройден. */}
                <div className="mb-3.5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-on-primary-container">
                      {t('lessonPlayer.stepOf', { step: activeIndex + 1, total: pages.length })}
                    </div>
                    <h1 className="mt-[7px] font-display text-[21px] font-bold leading-[1.2] text-on-surface">
                      {activeSection.is_homework ? t('lessonPlayer.homeworkTitle') : activeSection.title}
                    </h1>
                  </div>
                  {completed.has(activeSection.id) ? (
                    <span className="flex flex-none items-center gap-2 rounded-full bg-info-container px-[22px] py-[13px] text-[14.5px] font-bold text-info">
                      {t('lessonPlayer.sectionDone')}
                      <span className="material-symbols-outlined text-[16px]">check</span>
                    </span>
                  ) : review ? (
                    // В разборе раздел закрывать НЕЛЬЗЯ: отметка ушла бы
                    // ученику от лица куратора. Показываем состояние как есть.
                    <span className="flex flex-none items-center gap-2 rounded-full bg-surface-container px-[22px] py-[13px] text-[14.5px] font-bold text-on-surface-variant">
                      {t('lessonReview.sectionNotDone')}
                    </span>
                  ) : pendingSections.has(activeSection.id) ? (
                    <span className="flex flex-none items-center gap-2 rounded-full bg-surface-container px-[22px] py-[13px] text-[14.5px] font-bold text-on-surface-variant">
                      {t('lessonPlayer.sectionQueued')}
                      <span className="material-symbols-outlined text-[16px]">schedule</span>
                    </span>
                  ) : (
                    // Обычно раздел закрывается сам — когда просмотрен и все
                    // обязательные задания отвечены. Кнопка нужна разделам без
                    // заданий (одно видео, один текст): иначе их нечем закрыть.
                    <button
                      type="button"
                      onClick={markDone}
                      disabled={completeSection.isPending}
                      className="flex flex-none items-center gap-2 rounded-full bg-primary px-[22px] py-[13px] text-[14.5px] font-bold text-on-primary transition hover:bg-primary-hover disabled:opacity-60"
                    >
                      {t('lessonPlayer.markDone')}
                      <span className="material-symbols-outlined text-[16px]">check</span>
                    </button>
                  )}
                </div>

                {/* В разборе время не считается вовсе — полоса «учёт на паузе»
                    сообщала бы куратору о том, чего не происходит. */}
                {!review && !visible && (
                  <p className="mb-3 rounded-[16px] bg-surface-container px-4 py-2.5 text-[12.5px] text-on-surface-variant">
                    ⏸ {t('lessonPlayer.timePaused')}
                  </p>
                )}

                {syncStuck && (
                  <p className="mb-3 rounded-[16px] bg-error-container px-4 py-2.5 text-[12.5px] text-on-error-container">
                    {t('lessonPlayer.syncStuck')}
                  </p>
                )}

                <OpenTasksHoldNotice items={holding} onSelect={goToSection} />

                <SectionBlock
                  key={activeSection.id}
                  section={activeSection}
                  numbers={numbers}
                  complete={completed.has(activeSection.id)}
                  homework={activeSection.is_homework}
                  saved={savedMap}
                  pending={pendingBlocks}
                  review={review ? reviewMap : undefined}
                  onAnswer={review ? undefined : onAnswer}
                  missing={review ? undefined : missingBlocks}
                  notice={activeNotice}
                  hideHeader
                />

                {/* ⚠️ В разборе домашку НЕ сдают: оценка живёт в очереди
                    домашних работ, и второе место её выставления означало бы
                    вторую правду об оценке. */}
                {!review && activeSection.is_homework && (
                  <HomeworkSubmitPanel key="homework" section={activeSection} blockAnswers={blockAnswers} />
                )}

                <div className="mt-8 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    disabled={activeIndex === 0}
                    onClick={() => goToSection(pages[activeIndex - 1].id)}
                    className="flex items-center gap-2 rounded-full bg-surface-container px-5 py-[11px] text-[13px] font-semibold text-on-surface transition hover:bg-surface-container-high disabled:opacity-40"
                  >
                    <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                    {t('lessonPlayer.prevSection')}
                  </button>
                  <span className="shrink-0 text-[12.5px] font-semibold text-on-surface-variant tabular-nums">
                    {activeIndex + 1} / {pages.length}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      activeIndex < pages.length - 1
                        ? goToSection(pages[activeIndex + 1].id)
                        : exitLesson()
                    }
                    className="flex items-center gap-2 rounded-full bg-primary px-5 py-[11px] text-[13px] font-bold text-on-primary transition hover:bg-primary-hover"
                  >
                    {activeIndex < pages.length - 1 ? t('lessonPlayer.nextSection') : t('lessonPlayer.finishLesson')}
                    <span className="material-symbols-outlined text-[16px]">
                      {activeIndex < pages.length - 1 ? 'arrow_forward' : 'check'}
                    </span>
                  </button>
                </div>

                {allDone && (
                  <div className="mt-6 rounded-[22px] bg-primary-container px-5 py-6 text-center">
                    <p className="font-display text-2xl font-bold text-on-surface">🎉 {t('lessonPlayer.lessonComplete')}</p>
                    <p className="mt-1 text-[13px] text-on-surface-variant">{t('lessonPlayer.lessonCompleteHint')}</p>
                    <button
                      type="button"
                      onClick={() => exitLesson()}
                      className="mt-4 rounded-full bg-primary px-5 py-[11px] text-[13px] font-bold text-on-primary transition hover:bg-primary-hover"
                    >
                      {t('lessonPlayer.finishLesson')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {sections.length > 0 && (
            <div className="xl:sticky xl:top-6">
              <LessonSidebar
                sections={pages}
                activeId={activeSection?.id}
                completed={completed}
                onSelect={goToSection}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // `contents`: обёртка без собственной коробки — раскладка телефона та же, что с фрагментом, а тип корня совпадает
  // с веткой компьютера (см. «две раскладки — один путь» выше).
  return (
    <div className="contents">
      {review ? (
        // Разбор у сотрудника: одна шапка «назад · урок · чей · только просмотр». Прежде над плеером стояла карточка
        // разбора со своим «назад», и на 390 было две кнопки возврата подряд (docs/CURATOR-MOBILE-DESIGN.md §4.2).
        <MTitle
          back={backTo}
          title={lesson.data.title}
          subtitle={[
            review.studentName,
            t('lessonReview.readOnly'),
            t('lessonPlayer.sectionsProgress', { done: doneCount, total: normalSections.length }),
          ]
            .filter(Boolean)
            .join(' · ')}
        />
      ) : (
        <PageHeader
          title={lesson.data.title}
          subtitle={t('lessonPlayer.sectionsProgress', { done: doneCount, total: normalSections.length })}
          actions={
            <Link to={backTo} onClick={onExitLink}>
              <Button variant="ghost" size="sm">
                <span className="material-symbols-outlined">arrow_back</span>
                {t('lessonPlayer.backToLessons')}
              </Button>
            </Link>
          }
        />
      )}

      <div className="mb-6 h-3 w-full overflow-hidden rounded-pill bg-surface-container-highest">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${(doneCount / Math.max(normalSections.length, 1)) * 100}%` }}
        />
      </div>

      {!review && !visible && (
        <Card variant="subtle" className="mb-4 border-2 border-secondary-container/40">
          <p className="text-sm text-on-surface-variant">⏸ {t('lessonPlayer.timePaused')}</p>
        </Card>
      )}

      {syncStuck && (
        <Card variant="subtle" className="mb-4 border-2 border-error/40">
          <p className="text-sm text-error">{t('lessonPlayer.syncStuck')}</p>
        </Card>
      )}

      <OpenTasksHoldNotice items={holding} onSelect={goToSection} />

      <div key="lesson-grid" className="grid gap-6 lg:grid-cols-[1fr_260px]">
        <div key="lesson-content" ref={contentRef} className="min-w-0">
          {pages.length === 0 ? (
            <Card variant="subtle">{t('lessonPlayer.noSections')}</Card>
          ) : (
            <>
              <SectionBlock
                key={activeSection.id}
                section={activeSection}
                numbers={numbers}
                complete={completed.has(activeSection.id)}
                homework={activeSection.is_homework}
                saved={savedMap}
                pending={pendingBlocks}
                review={review ? reviewMap : undefined}
                onAnswer={review ? undefined : onAnswer}
                missing={review ? undefined : missingBlocks}
                notice={activeNotice}
              />

              {/* Homework section → one submission to the curator (essay + audio). */}
              {!review && activeSection.is_homework && (
                <HomeworkSubmitPanel key="homework" section={activeSection} blockAnswers={blockAnswers} />
              )}

              {/* Section pager — one section at a time, advance on the right (Edvibe-style). */}
              <div className="mt-8 flex items-center justify-between gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={activeIndex === 0}
                  onClick={() => goToSection(pages[activeIndex - 1].id)}
                >
                  <span className="material-symbols-outlined">arrow_back</span>
                  {t('lessonPlayer.prevSection')}
                </Button>
                <span className="shrink-0 text-sm font-medium text-on-surface-variant">
                  {activeIndex + 1} / {pages.length}
                </span>
                {activeIndex < pages.length - 1 ? (
                  <Button size="sm" onClick={() => goToSection(pages[activeIndex + 1].id)}>
                    {t('lessonPlayer.nextSection')}
                    <span className="material-symbols-outlined">arrow_forward</span>
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => exitLesson()}>
                    {t('lessonPlayer.finishLesson')}
                    <span className="material-symbols-outlined">check</span>
                  </Button>
                )}
              </div>

              {allDone && (
                <Card className="mt-6 text-center shadow-ambient">
                  <p className="font-display text-2xl font-bold tracking-display text-on-surface">🎉 {t('lessonPlayer.lessonComplete')}</p>
                  <p className="mt-1 text-sm text-on-surface-variant">{t('lessonPlayer.lessonCompleteHint')}</p>
                  <Button className="mt-4" onClick={() => exitLesson()}>{t('lessonPlayer.finishLesson')}</Button>
                </Card>
              )}
            </>
          )}
        </div>

        {sections.length > 0 && (
          // min-w-0 обязателен: без него колонка сетки брала полную ширину ленты фишек разделов (overflow-x-auto внутри
          // MChips), и разбор урока у куратора на 390 растягивался до 844 px (нашёл терминал 2, 13.10.2026).
          <aside className="order-first min-w-0 lg:order-last lg:sticky lg:top-6 lg:self-start">
            {review ? (
              // Разбор: разделы лентой фишек, а не карточкой-списком над уроком (§4.2) — ответы видны сразу.
              <MChips
                chips={pages.map((p) => ({ key: p.id, label: p.is_homework ? t('lessonPlayer.homeworkTitle') : p.title }))}
                active={activeSection?.id ?? ''}
                onChange={goToSection}
              />
            ) : (
              <SectionsNav sections={sections} activeId={activeId} onSelect={goToSection} />
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

function SectionBlock({
  section,
  numbers,
  complete,
  homework,
  saved,
  pending,
  onAnswer,
  hideHeader,
  review,
  missing,
  notice,
}: {
  hideHeader?: boolean;
  /** Работу раздела вернули или она ушла заново — плашка над заданиями. */
  notice?: SectionNotice | null;
  /** Задания, которые сервер назвал незаполненными при закрытии раздела (409). */
  missing?: Set<string>;
  section: Section;
  numbers: Map<string, string>;
  complete: boolean;
  homework?: boolean;
  saved: Record<string, { answer_data: string; is_correct: boolean }>;
  /** Блоки, чей ответ принят приложением, но ещё не подтверждён сервером. */
  pending: Set<string>;
  /**
   * Разбор ответов ученика. Есть — раздел читается, а не проходится.
   * `onAnswer` в этом случае не передаётся вовсе.
   */
  review?: Record<string, BlockReview>;
  onAnswer?: (
    blockId: string,
    payload: {
      answer_data: string;
      is_correct: boolean;
      block_type?: string;
      blob?: Blob;
      // Часы записи и причина остановки — только у голосового ответа.
      clientSeconds?: number;
      stopReason?: string;
      clientId?: string;
      stoppedAt?: number;
    },
  ) => void | Promise<boolean>;
}) {
  const { t } = useTranslation();
  const blocks: Block[] = useMemo(
    () => (section.blocks ?? []).slice().sort((a, b) => a.order_index - b.order_index),
    [section],
  );
  const missingHere = blocks.some((b) => missing?.has(b.id));

  return (
    <section data-section-id={section.id} className="scroll-mt-6">
      {/* На компьютере заголовок раздела рисует сама страница — по макету он
          идёт с надстрочником «Шаг N из M» и кнопкой «Отметить пройденным». */}
      <div className={cn('mb-4 flex items-center gap-2', hideHeader && 'hidden')}>
        {homework && <span className="material-symbols-outlined text-tertiary">home</span>}
        <h2 className="font-display text-2xl font-bold tracking-display text-on-surface">
          {homework ? t('lessonPlayer.homeworkTitle') : section.title}
        </h2>
        {complete && (
          <Pill variant="success">
            <span className="material-symbols-outlined text-sm">check</span>
            {t('lessonPlayer.sectionDone')}
          </Pill>
        )}
      </div>
      {section.deadline && (
        <Pill variant="warning">
          {t('lessonPlayer.deadline')}: {new Date(section.deadline).toLocaleString('ru-RU')}
        </Pill>
      )}

      {/* Сервер не закрыл раздел: открытые задания пустые (409 open_answers_missing).
          Говорим прямо и подсвечиваем сами задания — иначе раздел просто «не
          закрывается», и непонятно почему. */}
      {missingHere && (
        <p
          role="alert"
          className="mt-4 rounded-[16px] bg-error-container px-4 py-2.5 text-[12.5px] text-on-error-container"
        >
          {t('lessonPlayer.openAnswersMissing')}
        </p>
      )}

      {notice && <SectionResubmitNotice notice={notice} />}

      <div className="mt-4 ex-section">
        {blocks.map((b) => {
          // Заголовок бывает с разметкой — курсив, заливка (D39). Есть ли он, судим по
          // тексту без тегов: пустой `<br>` из редактора заголовком не считается.
          const rawTitle = (b.content as { title?: unknown })?.title;
          const blockTitle = titleText(rawTitle);
          return (
          <div
            key={b.id}
            className={cn(
              'ex-blockrow',
              // Скругление — как у карточки задания (20px), обводка вплотную к ней.
              missing?.has(b.id) && 'ex-blockrow--missing rounded-[20px] outline outline-2 outline-offset-2 outline-error/60',
            )}
          >
            {(numbers.get(b.id) || blockTitle) && (
              <div className="ex-head">
                {numbers.get(b.id) && <span className="ex-num">{numbers.get(b.id)}</span>}
                {blockTitle && <h3 className="ex-title" dangerouslySetInnerHTML={{ __html: titleHtml(rawTitle) }} />}
              </div>
            )}
            {/* ⚠️ Упавший блок не валит урок (13.10.2026): раньше ошибка одного
                задания на неожиданном содержимом заменяла ВЕСЬ урок экраном
                RouteErrorScreen. Теперь на месте блока — строка, остальное живёт. */}
            <BlockBoundary
              resetKey={b.content}
              fallback={<p className="text-sm text-on-surface-variant">{t('blockRenderers.blockFailed')}</p>}
            >
            <BlockView
              block={b}
              mode={review ? 'review' : 'student'}
              saved={saved[b.id]}
              pending={review ? undefined : pending?.has(b.id)}
              // Задание само скажет, что с ним не так (запись короче 5 с, пустое
              // сочинение), — общей фразы над разделом мало (11.09.2026).
              missing={missing?.has(b.id)}
              // «Не отвечал» ставим только тем заданиям, на которые ОТВЕТИТЬ
              // МОЖНО: пустая заготовка и текстовый блок молчат, а не выглядят
              // пропущенными учеником.
              review={
                review
                  ? review[b.id] ??
                    (ANSWERABLE_TYPES.has(b.type) && blockHasContent(b)
                      ? { attempts: 0, unanswered: true }
                      : undefined)
                  : undefined
              }
              onAnswer={
                review
                  ? undefined
                  : ({ is_correct, answer_data, blob, clientSeconds, stopReason, clientId, stoppedAt }) =>
                      onAnswer?.(b.id, {
                        answer_data,
                        is_correct,
                        block_type: b.type,
                        blob,
                        clientSeconds,
                        stopReason,
                        clientId,
                        stoppedAt,
                      })
              }
            />
            </BlockBoundary>
          </div>
          );
        })}
      </div>
    </section>
  );
}

// HomeworkSubmitPanel — bundles the homework section's essay text + recorded
// audio into ONE submission the curator grades (0–10). Shows the current status
// once submitted; lets the student resubmit when it was sent back for revision.
function HomeworkSubmitPanel({
  section,
  blockAnswers,
}: {
  section: Section;
  blockAnswers: Record<string, { answer_data: string; block_type?: string }>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const mine = useQuery({
    queryKey: ['student', 'homework', 'mine'],
    queryFn: () => progressApi.listMyHomework(undefined, 0, 100),
  });
  const submission = mine.data?.items.find((h) => h.section_id === section.id);
  // `review_note`, раньше `curator_note` (договор student-api.md §4).
  const reviewNote = submission ? reviewNoteOf(submission) : '';

  const blocks = section.blocks ?? [];
  // «Ответ по пунктам» — нумерованными строками после сочинений, как сдачу собирает сервер
  // (progress.homework_section_content, 000027).
  const essayText = [
    ...blocks.filter((b) => b.type === 'essay' || b.type === 'text').map((b) => blockAnswers[b.id]?.answer_data),
    ...blocks.filter((b) => b.type === 'open_items').map((b) => openItemsAnswerText(blockAnswers[b.id]?.answer_data)),
  ]
    .filter(Boolean)
    .join('\n\n');
  const fileURLs = blocks
    .filter((b) => b.type === 'audio_record')
    .map((b) => blockAnswers[b.id]?.answer_data)
    .filter((u): u is string => !!u);
  const hasContent = essayText.trim().length > 0 || fileURLs.length > 0;

  const submit = useMutation({
    mutationFn: () =>
      progressApi.submitHomework({
        section_id: section.id,
        // `content` is required server-side; fall back to a note for audio-only.
        content: essayText.trim() || t('hwSubmit.voiceAnswer'),
        file_urls: fileURLs,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student', 'homework', 'mine'] }),
  });

  const canSubmit = !submission || submission.status === 'revision' || submission.status === 'rejected';

  const STATUS: Record<string, { variant: 'primary' | 'success' | 'warning' | 'danger'; label: string }> = {
    pending: { variant: 'primary', label: t('hwSubmit.statusPending') },
    approved: { variant: 'success', label: t('hwSubmit.statusApproved') },
    revision: { variant: 'warning', label: t('hwSubmit.statusRevision') },
    rejected: { variant: 'danger', label: t('hwSubmit.statusRejected') },
  };

  return (
    <Card className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>{t('hwSubmit.title')}</CardTitle>
        <div className="flex items-center gap-2">
          {submission && (
            <Pill variant={(STATUS[submission.status] ?? STATUS.pending).variant}>
              {/* Вернулась в очередь после доработки — так и говорим (договор §2). */}
              {submission.status === 'pending' && submission.resubmitted
                ? t('hwSubmit.statusResent')
                : (STATUS[submission.status] ?? STATUS.pending).label}
            </Pill>
          )}
        </div>
      </div>

      {reviewNote && (
        <p className="mt-2 rounded-card bg-surface-container-low p-3 text-sm text-on-surface-variant">
          <b>{t('hwSubmit.curatorNote')}</b> {reviewNote}
        </p>
      )}

      {canSubmit ? (
        <>
          <p className="mt-2 text-sm text-on-surface-variant">
            {t('hwSubmit.sectionNote')}
          </p>
          {submit.isError && <p className="mt-2 text-sm text-error">{humanizeError(submit.error)}</p>}
          <Button className="mt-3" loading={submit.isPending} disabled={!hasContent} onClick={() => submit.mutate()}>
            <span className="material-symbols-outlined">send</span>
            {t(submission ? 'hwSubmit.resubmit' : 'hwSubmit.submit')}
          </Button>
          {!hasContent && (
            <p className="mt-2 text-xs text-on-surface-variant">{t('hwSubmit.fillFirst')}</p>
          )}
        </>
      ) : (
        <p className="mt-2 text-sm text-success">{t('hwSubmit.sentToCurator')}</p>
      )}
    </Card>
  );
}
